import { useState, useCallback } from 'react'
import { useAuth } from './useAuth'

const CATS_KEY  = 'yokan_timeline_cat_settings'
const TOAST_KEY = 'yokan_timeline_toast_enabled'

// Both keys are account-scoped inside localStorage, so the prefs are per
// device *and* per account: the phone can silence toasts while the desktop
// keeps them, and two accounts on the same browser don't share settings.
const scoped = (base, account) => `${base}:${account}`

function loadCats(account) {
  try {
    const raw = localStorage.getItem(scoped(CATS_KEY, account))
      ?? localStorage.getItem(CATS_KEY) // legacy shared value → first read migrates it
    return JSON.parse(raw) ?? {}
  } catch { return {} }
}

function saveCats(account, v) {
  try { localStorage.setItem(scoped(CATS_KEY, account), JSON.stringify(v)) } catch {}
}

function loadToast(account) {
  try { return localStorage.getItem(scoped(TOAST_KEY, account)) !== '0' } catch { return true }
}

function saveToast(account, v) {
  try { localStorage.setItem(scoped(TOAST_KEY, account), v ? '1' : '0') } catch {}
}

const ALL_CATS = ['Solar','Klima','Fahrzeug','Sicherheit','Person','Tier','Batterie','System','Energie','Haus']

export function useCategorySettings() {
  const { user } = useAuth()
  const account  = user?.email ?? 'anon'

  const [settings, setSettings]         = useState(() => loadCats('anon'))
  const [toastEnabled, setToastEnabled] = useState(() => loadToast('anon'))

  // /auth/me resolves after mount — re-read once the real account is known.
  const [prevAccount, setPrevAccount] = useState(account)
  if (account !== prevAccount) {
    setPrevAccount(account)
    setSettings(loadCats(account))
    setToastEnabled(loadToast(account))
  }

  const toggle = useCallback((cat, key) => {
    setSettings(prev => {
      const cur  = { visible: true, notify: true, ...(prev[cat] ?? {}) }
      const next = { ...prev, [cat]: { ...cur, [key]: !cur[key] } }
      saveCats(account, next)
      return next
    })
  }, [account])

  const toggleToast = useCallback(() => {
    setToastEnabled(prev => {
      const next = !prev
      saveToast(account, next)
      return next
    })
  }, [account])

  const hiddenCategories = ALL_CATS.filter(c => (settings[c]?.visible ?? true) === false)
  const silentCategories = new Set(ALL_CATS.filter(c => (settings[c]?.notify ?? true) === false))

  return { settings, toggle, hiddenCategories, silentCategories, toastEnabled, toggleToast }
}
